import React, { useState } from 'react';
import theme from './ProjectModules.module.scss';
import { BygrProject } from '@/types';
import { AlertBanner, Button, ButtonBar, Card } from '@ygtechnologies/rubus-ui';
import { FollowerRecalculationPreview, previewRecalculateFollowers, recalculateFollowers } from '@/services/projects';
import DocumentCategorisationTool from './DocumentCategorisationTool';

interface ProjectToolsProps {
    project: BygrProject;
}

const ProjectTools: React.FC<ProjectToolsProps> = ({ project }) => {
    const [preview, setPreview] = useState<FollowerRecalculationPreview | null>(null);
    const [isBusy, setIsBusy] = useState(false);
    const [queued, setQueued] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = async (action: () => Promise<void>) => {
        setIsBusy(true);
        setError(null);
        try {
            await action();
        } catch {
            setError('The request failed. Check that the AM backend is reachable.');
        } finally {
            setIsBusy(false);
        }
    };

    const onPreview = () =>
        run(async () => {
            setQueued(false);
            setPreview(await previewRecalculateFollowers(project.id));
        });

    const onApply = () =>
        run(async () => {
            await recalculateFollowers(project.id);
            setPreview(null);
            setQueued(true);
        });

    return (
        <div className={theme.modules}>
            <Card variant="flat">
                <h4 className={theme.cardTitle}>Recalculate chat followers</h4>
                <p>
                    Rebuilds who follows each case and unit chat in this project. Run this when a user gets no yellow dots or
                    no unread reminder emails: a follow that was wrongly removed stays removed until it is recalculated.
                    Revived follows start counting from now, so this does not resurface old messages as unread.
                </p>
                <ButtonBar margins="bottom">
                    <Button variant="secondary" size="md" onClick={onPreview} disabled={isBusy}>
                        Dry run
                    </Button>
                    <Button variant="primary" size="md" onClick={onApply} disabled={isBusy}>
                        Recalculate
                    </Button>
                </ButtonBar>

                {preview && (
                    <AlertBanner type="info">
                        Would revive {preview.wouldRevive}, add {preview.wouldAdd}, remove {preview.wouldRemove}.{' '}
                        {preview.unchanged} already correct. Nothing has been written yet.
                    </AlertBanner>
                )}
                {queued && (
                    <AlertBanner type="success">
                        Queued. It runs as a background job on the AM backend, so there is nothing to wait for here. Large
                        projects can take a while.
                    </AlertBanner>
                )}
                {error && <AlertBanner type="danger">{error}</AlertBanner>}
            </Card>
            <DocumentCategorisationTool project={project} />
        </div>
    );
};

export default ProjectTools;
